import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createSelector } from '@reduxjs/toolkit';
import type { RootState } from '.';
import type { BestPractice } from '../components/bestPractices/types';
import type { Domain } from '@server/domain';

export interface BestPracticesState {
  data?: BestPractice[];
  isLoading: boolean;
}

const initialState: BestPracticesState = {
  data: undefined,
  isLoading: false,
};

export const bestPracticesSlice = createSlice({
  name: 'bestPractices',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      state.isLoading = action.payload.isLoading;
    },
    setBestPractices: (state, action: PayloadAction<{ bestPractices: BestPractice[] }>) => {
      state.data = action.payload.bestPractices;
      state.isLoading = false;
    },
    updateBestPractice: (
      state,
      action: PayloadAction<{
        id: string;
        update: Partial<BestPractice>;
      }>,
    ) => {
      const { id, update } = action.payload;
      state.data = state.data?.map((item) => {
        if (item.id === id) {
          const updated = { ...item, ...update, updatedAt: new Date().toISOString() };
          return updated;
        }
        return item;
      });
    },
    deleteBestPractice: (state, action: PayloadAction<{ bestPracticeId: string }>) => {
      state.data = state.data?.filter(
        (bestPractice) => bestPractice.id !== action.payload.bestPracticeId,
      );
    },
    addBestPractice: (
      state,
      action: PayloadAction<{ bestPractice: BestPractice; index?: number }>,
    ) => {
      const { bestPractice, index } = action.payload;
      if (index !== undefined && index >= 0 && state.data && index <= state.data.length) {
        state.data = [...state.data?.slice(0, index), bestPractice, ...state.data?.slice(index)];
      } else {
        state.data = [...(state.data ?? []), bestPractice];
      }
    },
  },
});

// Base selectors
const selectBestPracticesData = (state: RootState) => state.bestPractices.data;

// Memoized selectors
export const selectSweepBestPractices = createSelector(
  [selectBestPracticesData, (_state: RootState, domain?: Domain) => domain],
  (bestPracticesData, domain) => {
    const bestPractices = bestPracticesData?.filter((bp: BestPractice) => bp.isSystem);
    return domain
      ? bestPractices?.filter((bp: BestPractice) => bp.applicableDomain === domain)
      : bestPractices;
  },
);

export const selectCustomBestPractices = createSelector(
  [selectBestPracticesData, (_state: RootState, domain?: Domain) => domain],
  (bestPracticesData, domain) => {
    const bestPractices = bestPracticesData?.filter((bp: BestPractice) => !bp.isSystem);
    return domain
      ? bestPractices?.filter((bp: BestPractice) => bp.applicableDomain === domain)
      : bestPractices;
  },
);

export const selectIsLoadingBestPractices = (state: RootState) =>
  state.bestPractices.isLoading || false;

export const {
  setLoading,
  setBestPractices,
  updateBestPractice,
  deleteBestPractice,
  addBestPractice,
} = bestPracticesSlice.actions;

export default bestPracticesSlice.reducer;
